import React, { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppRoutes } from './AppRoutes';
import ProtectedRoute from "../components/CommonElements/ProtectedRoutes/ProtectedRoute";
import { getAuthToken } from "../utils/authHelpers";
import PublicRoutes from '../components/CommonElements/ProtectedRoutes/PublicRoutes';

const token = getAuthToken();

const LazyLoad = (Component) => (
  <Suspense fallback={<div />}>
    <Component />
  </Suspense>
);

// Lazy imports
const LandingPage = lazy(() => import("../pages/LandingPage/LandingPage"));
const Login = lazy(() => import("../components/Authentication/Login"));
const FindJobPage = lazy(() => import("../pages/GuestUsers/FindJobPage"));
const JobDetails = lazy(() => import("../pages/GuestUsers/FindJobPage/JobDetails"));
const JobSeekerDashboard = lazy(() => import("../pages/Candidates/JobSeekerDashboard"));
const UserInfo = lazy(() => import("../pages/UserInfoPage/UserInfo"));
const RecommendedJobs = lazy(() => import("../pages/Candidates/RecommendedJobs/RecommendedJobs"));
const SavedJobs = lazy(() => import("../pages/Candidates/SaveJobs/SavedJobs"));
const Blogs = lazy(() => import("../pages/Candidates/Blogs/Blogs"));
const RefrencePeople = lazy(() => import("../pages/Candidates/ReferencePeople/ReferencePeople"));
const Setting = lazy(() => import("../pages/Candidates/SettingPage/Setting"));
const PrivacyPolicy = lazy(() => import("../components/Footer/PrivacyPolicy"));
const ReportProblem = lazy(() => import("../components/ReportProblem/ReportProblemPage"));
const About = lazy(() => import("../pages/GuestUsers/About/About"));
const ContactUsPage = lazy(() => import("../pages/GuestUsers/ContactUsPage"));
const Dashboard = lazy(() => import("../pages/Organizations/Dashboard/Dashboard"));
const OrganizationInfo = lazy(() => import("../pages/Organizations/OrganizationInfo/OrganizationInfo"));
const JobStatus = lazy(() => import("../pages/Organizations/JobStatus/JobStatus"));
const RecommendCandidate = lazy(() => import("../pages/Organizations/RecommendCandidate/RecommendCandidate"));
const Analytics = lazy(() => import("../pages/Organizations/Analytics/Analytics"));
const ChatPage = lazy(() => import("../pages/Candidates/Chat/ChatPage"));
const OrganizationProfile = lazy(() => import("../components/OrganizationProfile"));
const OrgSettings = lazy(() => import("../pages/Organizations/Settings/Settings"));
const CandidateDetails = lazy(() => import("../pages/Organizations/JobStatus/CandidateDetails"));
const CandidateProfilePage = lazy(() => import("../pages/Organizations/JobStatus/CandidateProfilePage"));
const CandidateProfile = lazy(() => import("../pages/Organizations/RecommendCandidate/CandidateProfile"));
const UserCalender = lazy(() => import("../pages/Candidates/Calender/UserCalender"));
const SearchedJobs = lazy(() => import("../components/CommonElements/SearchedJobs"));
const OrgCalendar = lazy(() => import("../pages/Organizations/OrgSetting/OrgSettingPage"));
// const Tickets = lazy(() => import("../pages/Candidates/Tickets/Tickets"));
const AbusePolicies = lazy(() => import("../components/Footer/AbusePolicies"));
const TermandConditions = lazy(() => import("../components/Footer/TermandConditions"));
const CookiePolicy = lazy(() => import("../components/Footer/CookiesPolicies"));
const TradmarkPolicy = lazy(() => import("../components/Footer/TradmarkPolicy"));
const ResetOrgPassword = lazy(() => import("../components/ResetPasswordOrg/ResetOrgPassword"));
const ReferralJobPage = lazy(() => import("../pages/GuestUsers/FindJobPage/ReferralJobPage"));
const RequestDemo = lazy(() => import("../pages/GuestUsers/RequestDemo/RequestDemo"));
const CheckoutWrapper = lazy(() => import("../components/CheckoutWrapper"));
const PaymentSuccessReturn = lazy(() => import("../components/PaymentSuccessReturn"));
const PageNotFound = lazy(() => import("../components/PageNotFound/PageNotFound"));
const HelpCenter = lazy(() => import("../pages/Candidates/HelpCenter/HelpCenter"));
const PricingPage = lazy(() => import("../pages/GuestUsers/PricingPage"));

const routes = createBrowserRouter([
  {
    path: AppRoutes.PUBLIC_ROUTES.HOME,
    element: LazyLoad(LandingPage),
    SideNav: false,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.LOGIN,
    element: <PublicRoutes>{LazyLoad(Login)}</PublicRoutes>,
    SideNav: false,
    nav: false,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.SIGN_UP,
    element: <PublicRoutes>{LazyLoad(Login)}</PublicRoutes>,
    SideNav: false,
    nav: false,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.FIND_JOB,
    element: <PublicRoutes>{LazyLoad(FindJobPage)}</PublicRoutes>,
    SideNav: token ? true : false,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.APPLICATION_STATUS,
    element: <ProtectedRoute allowedRole="employee">{LazyLoad(JobSeekerDashboard)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.USER_INFO,
    element: <ProtectedRoute allowedRole="employee"> {LazyLoad(UserInfo)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.SPECIFIC_JOB_DETAILS,
    element: <ProtectedRoute> {LazyLoad(JobDetails)}</ProtectedRoute>,
    SideNav: token ? true : false,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.RECOMENDED_JOBS,
    element: <ProtectedRoute allowedRole="employee"> {LazyLoad(RecommendedJobs)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.SAVE_JOBS,
    element: <ProtectedRoute allowedRole="employee"> {LazyLoad(SavedJobs)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.PRICING_PAGE,
    element: <>{LazyLoad(PricingPage)}</>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.BLOGS,
    element: <ProtectedRoute allowedRole="employee"> {LazyLoad(Blogs)}</ProtectedRoute>,
    SideNav: true,
    nav: true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.REFERENCE_PEOPLE,
    element: <ProtectedRoute allowedRole="employee"> {LazyLoad(RefrencePeople)} </ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.SETTING,
    element: <ProtectedRoute allowedRole="employee"> {LazyLoad(Setting)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.PRIVACYPOLICY,
    element: LazyLoad(PrivacyPolicy),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.REPORTPROBLEM,
    element: LazyLoad(ReportProblem),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.ABOUTUS,
    element: LazyLoad(About),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.CONTACT_US,
    element: LazyLoad(ContactUsPage),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.DASHBOARD,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(Dashboard)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.ORGANIZATION_INFO,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(OrganizationInfo)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.JOB_STATUS,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(JobStatus)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.RECOMMEND_CANDIDATE,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(RecommendCandidate)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.ANALYTICS,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(Analytics)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.CHAT_PAGE,
    element: <ProtectedRoute>{LazyLoad(ChatPage)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.ORGANIZATION_PROFILE,
    element: <>{LazyLoad(OrganizationProfile)}</>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.ORG_SETTINGS,
    element: <ProtectedRoute allowedRole="organization"> {LazyLoad(OrgSettings)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.CANDIDATE_DETAILS,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(CandidateDetails)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.CANDIDATE_DETAILS_INFO_PAGE,
    element: <ProtectedRoute>{LazyLoad(CandidateProfilePage)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.RECOMMEND_CANDIDATE_PROFILE,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(CandidateProfile)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.FIND_JOB_CALENDER,
    element: <ProtectedRoute allowedRole="employee">{LazyLoad(UserCalender)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.JOBLISTING,
    element: <ProtectedRoute>{LazyLoad(SearchedJobs)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.ORGANIZATION_ROUTES.ORG_CALENDER,
    element: <ProtectedRoute allowedRole="organization">{LazyLoad(OrgCalendar)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  // {
  //   path: AppRoutes.JOBSEEKER_ROUTES.TICKETS,
  //   element: <ProtectedRoute allowedRole="employee">{LazyLoad(Tickets)}</ProtectedRoute>,
  //   SideNav: true,
  //   nav: true
  // },
  {
    path: AppRoutes.JOBSEEKER_ROUTES.HelpCenter,
    element: <ProtectedRoute allowedRole="employee">{LazyLoad(HelpCenter)}</ProtectedRoute>,
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.ABUSEPOLICIES,
    element: LazyLoad(AbusePolicies),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.TERMANDCONDITIONS,
    element: LazyLoad(TermandConditions),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.COOKIESPOLICY,
    element: LazyLoad(CookiePolicy),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.TRADMARKPOLICY,
    element: LazyLoad(TradmarkPolicy),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.RESET_PASSWORD,
    element: LazyLoad(ResetOrgPassword),
    SideNav: false,
    nav: false,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.REFFERAL_PAGE,
    element: LazyLoad(ReferralJobPage),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.REQUEST_DEMO_PAGE,
    element: LazyLoad(RequestDemo),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.CHECKOUT_PAGE,
    element: LazyLoad(CheckoutWrapper),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.PAYMENT_SUCCESSFULL,
    element: LazyLoad(PaymentSuccessReturn),
    SideNav: true,
    nav: true,
    footer:true
  },
  {
    path: AppRoutes.PUBLIC_ROUTES.NOTEXIST,
    element: LazyLoad(PageNotFound),
    SideNav: false,
    nav: false,
    footer:true
  },

]);

export default routes;
